import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Trash2, ImageIcon } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Certificados() {
  const [certs, setCerts] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const fetchCerts = () => api.get("/certificates").then((r) => setCerts(r.data)).catch(() => {});
  useEffect(() => { fetchCerts(); }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Escolha uma imagem (JPG, PNG ou WebP)");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Escolha a foto do certificado");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/certificates?title=${encodeURIComponent(title.trim())}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificado publicado no site!");
      setTitle("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchCerts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Retirar o certificado "${c.title}" do site? A foto continua guardada; o card sai do carrossel.`)) return;
    try {
      await api.delete(`/certificates/${c.id}`);
      toast.success("Certificado removido do carrossel");
      fetchCerts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="certificados-page">
      <h1 className="font-display text-3xl sm:text-4xl font-light mb-2">Certificados</h1>
      <p className="text-sm text-stone-500 mb-6">Os certificados cadastrados aparecem no carrossel da página inicial, na ordem de envio.</p>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Upload */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] h-fit">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279] mb-4">Novo certificado</p>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${drag ? "border-[#C8A279] bg-[#F3EFE6]" : "border-stone-300 hover:border-[#C8A279]"}`}
            data-testid="certificados-dropzone"
          >
            <UploadCloud size={28} className="mx-auto text-stone-400" />
            {file ? (
              <p className="mt-3 text-sm font-medium text-stone-700" data-testid="certificados-file-name">{file.name}</p>
            ) : (
              <p className="mt-3 text-sm text-stone-500">Arraste a foto aqui ou <span className="text-[#B38F68] font-medium underline">escolha o arquivo</span></p>
            )}
            <p className="mt-2 text-xs text-stone-400">JPG, PNG ou WebP — até 10 MB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickFile(e.target.files[0])} data-testid="certificados-file-input" />
          <div className="mt-4">
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Título</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Formação de Colorista — Keune"
              className="mt-1.5 w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all"
              data-testid="certificados-title-input"
            />
          </div>
          <button
            onClick={upload}
            disabled={busy}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#121212] text-white rounded-full py-3 text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50"
            data-testid="certificados-upload-button"
          >
            <UploadCloud size={15} /> {busy ? "Enviando…" : "Publicar no site"}
          </button>
        </div>

        {/* Lista */}
        <div className="lg:col-span-2">
          {certs.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-14 text-center h-full flex flex-col items-center justify-center">
              <ImageIcon size={32} className="text-stone-300 mb-3" />
              <p className="text-sm text-stone-400">Nenhum certificado publicado ainda.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="certificados-grid">
              {certs.map((c, i) => (
                <div key={c.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)] group" data-testid={`certificados-card-${i}`}>
                  <div className="bg-white h-40 flex items-center justify-center overflow-hidden">
                    <img src={c.url} alt={c.title} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between gap-2 border-t border-stone-100">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#C8A279]">{String(i + 1).padStart(2, "0")}</p>
                      <p className="text-sm font-medium truncate">{c.title}</p>
                    </div>
                    <button
                      onClick={() => remove(c)}
                      className="p-2 rounded-full text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                      data-testid={`certificados-delete-${c.id}`}
                      aria-label={`Remover ${c.title}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
